import { UseQueryResult } from "@tanstack/react-query";
import {
  resetOrderIfLimited,
  resetOrderToMostRelevantIfApplicable
} from "PFApp/booking/parts/overview/use_tab_data.utils";
import { CalendarRoot } from "PFApp/booking/types/calendar_root";
import {
  useApiFilters,
  useOnFiltersUpdate,
  useOnFilterUpdate,
  useSyncFiltersV2Meta
} from "PFApp/components/filters_v2";
import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import {
  ProjectsResponse,
  useProjectsEntries
} from "PFCore/hooks/queries/bookings/projects/use_projects_entries";
import { useProjectsInvalidate } from "PFCore/hooks/queries/bookings/projects/use_projects_invalidate";
import { OrderOption } from "PFTypes";
import { KnownFiltersV2 } from "PFTypes/filters_v2";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useUnmount } from "react-use";

const PER_PAGE = 15;

type UseProjectsData = {
  enabled: boolean;
  calendarRoot: CalendarRoot;
  page: number;
  resetPagination: VoidFunction;
  selectedOrder: OrderOption | null;
  setSelectedOrder: (order: OrderOption | null) => void;
};

export const useProjectsData = ({
  enabled,
  calendarRoot,
  page,
  resetPagination,
  selectedOrder,
  setSelectedOrder
}: UseProjectsData): UseQueryResult<ProjectsResponse> => {
  const { showGrowl } = useGrowlStoreActions();
  const { t } = useTranslation("bookingModule");
  const { apiFilters, areApiFiltersReady } = useApiFilters();

  useOnFilterUpdate(KnownFiltersV2.Term, (filter) => {
    if (!enabled || filter.isChildren) {
      return;
    }

    resetOrderToMostRelevantIfApplicable(filter, selectedOrder, setSelectedOrder);
  });

  useOnFiltersUpdate(() => {
    if (!enabled) {
      return;
    }
    resetPagination();
    resetOrderIfLimited(selectedOrder, () => {
      setSelectedOrder(null);
      showGrowl({
        message: t("sortingReset"),
        kind: "alert"
      });
    });
  });

  const query = useProjectsEntries({
    page,
    calendarRoot,
    perPage: PER_PAGE,
    order: selectedOrder,
    filters: apiFilters,
    options: {
      enabled: enabled && areApiFiltersReady
    }
  });
  const { data } = query;

  const syncFiltersV2 = useSyncFiltersV2Meta();

  useEffect(() => {
    if (!enabled) {
      return;
    }

    syncFiltersV2(data?.meta);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.meta, enabled, apiFilters]);

  const { removeCache: removeProjectsCache } = useProjectsInvalidate();
  useUnmount(removeProjectsCache);

  return query;
};
